/*
 * Back-in-stock dialog — shared by the PDP, the colour map and the bundle
 * builder.
 *
 * Progressive enhancement, not a replacement: every trigger is a real link
 * to somewhere useful, and this only intercepts the click when it can
 * actually do better. If the script fails to load, the links still work.
 *
 * The signup is posted with fetch rather than submitted, because a native
 * submit reloads the page and there would be no dialog left to confirm in.
 * The payload is exactly what Shopify's own customer form sends, so the
 * customer lands in Admin tagged `back-in-stock` plus the product handle —
 * which is what lets a restock email go to the people who wanted THAT
 * colour instead of everyone waiting on anything.
 */
(function () {
  'use strict';

  var CLOSE_AFTER = 2600; // ms the confirmation stays up before it closes

  var dialog = document.querySelector('[data-notify-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var form = dialog.querySelector('[data-notify-form]');
  var tags = dialog.querySelector('[data-notify-tags]');
  var email = dialog.querySelector('[data-notify-email]');
  var submit = dialog.querySelector('[data-notify-submit]');
  var errorEl = dialog.querySelector('[data-notify-error]');
  var askEl = dialog.querySelector('[data-notify-ask]');
  var doneEl = dialog.querySelector('[data-notify-done]');
  var doneText = dialog.querySelector('[data-notify-done-text]');
  var nameEl = dialog.querySelector('[data-notify-name]');
  var returnTo = dialog.querySelector('[data-notify-return]');
  if (!form || !tags || !email) return;

  var opener = null;
  var closeTimer = null;
  var label = '';
  var currentHandle = '';

  /*
   * Klaviyo's native Back in Stock flow triggers on its own subscription
   * event, not on Shopify customer tags — so alongside the customer-form
   * post (which feeds the per-colour segments), the submit also registers
   * a Klaviyo back-in-stock subscription for the product's variant. Fire-
   * and-forget with keepalive so the captcha hand-off's navigation cannot
   * cancel it; a failure here never blocks the signup the shopper sees.
   * The public key comes from the Klaviyo app embed's own script tag, so
   * it follows the account; the literal is only a fallback.
   */
  var KLAVIYO_KEY = (function () {
    var s = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
    var m = s && s.src.match(/company_id=([A-Za-z0-9]+)/);
    return m ? m[1] : 'XBC3iq';
  })();
  var KLAVIYO_LIST = 'TnyJbE'; // "Back in Stock Signups" — single opt-in

  function klaviyoPost(path, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_KEY, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
      body: JSON.stringify(payload)
    });
  }

  /*
   * The variant id is resolved when the dialog OPENS, not on submit: the
   * captcha hand-off navigates the page moments after submit, and while
   * both Klaviyo posts survive that (keepalive), a product fetch between
   * them does not — which is how a phone signup got consent but no BIS
   * subscription. By submit time the id is already here.
   */
  var variantIdPromise = null;

  function prefetchVariantId(handle) {
    if (!handle) { variantIdPromise = null; return; }
    variantIdPromise = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        // Single-variant bags today; the first variant IS the product.
        var variant = product && product.variants && product.variants[0];
        return variant ? variant.id : null;
      })
      .catch(function () { return null; });
  }

  function subscribeBackInStock(emailValue) {
    if (!emailValue || !KLAVIYO_KEY || !variantIdPromise) return;
    /*
     * Consent first, subscription second. A profile born from the BIS call
     * alone has no marketing consent and this account suppresses it on the
     * spot — and suppression blocks even the back-in-stock send itself. The
     * list subscribe records the same consent the Shopify customer-form
     * post already claims (accepts_marketing), so the profile exists as
     * consented before the BIS subscription does.
     */
    var consent = klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock modal',
          profile: { data: { type: 'profile', attributes: {
            email: emailValue,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
      }
    }).catch(function () { /* consent is best-effort; the BIS call still goes out */ });

    /*
     * Resolves once the BIS request has been DISPATCHED (keepalive carries
     * it through any navigation) — the caller uses this to hold the captcha
     * hand-off just long enough that the request exists at all.
     */
    return Promise.all([consent, variantIdPromise])
      .then(function (results) {
        var variantId = results[1];
        if (!variantId) return;
        klaviyoPost('back-in-stock-subscriptions', {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: emailValue } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        }).catch(function () { /* additive path: the tagged signup still lands */ });
      })
      .catch(function () { /* additive path: the tagged signup still lands */ });
  }

  function reset() {
    clearTimeout(closeTimer);
    dialog.removeAttribute('data-closing');
    askEl.hidden = false;
    doneEl.hidden = true;
    errorEl.hidden = true;
    errorEl.textContent = '';
    submit.disabled = false;
    form.reset();
  }

  function open(handle, name, trigger) {
    reset();
    opener = trigger || null;
    label = name || '';
    // `back-in-stock` is the audience, the handle is which bag they waited
    // for. A pair with no product yet has no handle, and that is fine — the
    // signup still lands, it just is not colour-scoped.
    tags.value = handle ? 'back-in-stock,' + handle : 'back-in-stock';
    currentHandle = handle || '';
    prefetchVariantId(currentHandle);
    if (returnTo) returnTo.value = location.pathname + location.search;
    if (nameEl) nameEl.textContent = label ? 'the ' + label + ' combo' : 'this combo';
    dialog.showModal();
    // Focus the field, not the close button the browser would pick.
    requestAnimationFrame(function () { email.focus(); });
  }

  function close() {
    clearTimeout(closeTimer);
    dialog.setAttribute('data-closing', '');
    setTimeout(function () {
      dialog.close();
      dialog.removeAttribute('data-closing');
    }, 250);
  }

  dialog.addEventListener('close', function () {
    dialog.removeAttribute('data-closing');
    if (opener && opener.focus) opener.focus();
    opener = null;
  });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-notify-open]') : null;
    if (trigger) {
      e.preventDefault();
      open(trigger.getAttribute('data-notify-handle'),
           trigger.getAttribute('data-notify-label'),
           trigger);
      return;
    }
    if (e.target.closest && e.target.closest('[data-notify-close]')) close();
  });

  // Clicking the backdrop closes: the dialog element itself is the backdrop,
  // so a click landing on it rather than on its contents means "outside".
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog) close();
  });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (submit.disabled) return;
    submit.disabled = true;
    errorEl.hidden = true;

    var klaviyoDispatched = subscribeBackInStock(email.value) || Promise.resolve();

    fetch(form.getAttribute('action'), {
      method: 'POST',
      body: new FormData(form),
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        return r.text().then(function (body) {
          /*
           * Three outcomes, not two. Shopify's bot protection challenges
           * background posts to /contact with an hCaptcha page (403 here,
           * observed directly) — and a fetch can never solve a captcha, so
           * retrying in the dialog is a dead end BY DESIGN, not a blip. The
           * only path through a challenge is a real navigation: hand the
           * SAME form to the browser, let Shopify present its challenge,
           * and have return_to bring the shopper back. State is parked in
           * sessionStorage so the confirmation can greet them on landing.
           */
          var challenged = r.status === 403 || r.status === 429 ||
            /challenge|hcaptcha|captcha/i.test(body);
          if (challenged) {
            try {
              sessionStorage.setItem('cubedNotifyPending', JSON.stringify({ label: label }));
            } catch (err) { /* private mode: the signup still works */ }
            /*
             * Hold the challenge navigation until the Klaviyo posts have
             * been dispatched (capped so a stalled request can't trap the
             * shopper) — navigating sooner cancels work keepalive would
             * otherwise finish.
             */
            Promise.race([
              klaviyoDispatched,
              new Promise(function (res) { setTimeout(res, 800); })
            ]).then(function () { form.submit(); });
            return null;
          }
          if (!r.ok) throw new Error('signup failed');
          return body;
        });
      })
      .then(function (body) {
        if (body === null) return; // navigating through the challenge
        askEl.hidden = true;
        doneEl.hidden = false;
        if (doneText) {
          doneText.textContent = label
            ? "You'll be the first to know when the " + label + ' combo is back in stock!'
            : "You'll be the first to know when it's back in stock!";
        }
        closeTimer = setTimeout(close, CLOSE_AFTER);
      })
      .catch(function () {
        submit.disabled = false;
        errorEl.hidden = false;
        errorEl.textContent = "Couldn't sign you up just now. Please try again.";
      });
  });

  /*
   * The far side of the native round-trip: Shopify has accepted the signup
   * (challenge and all) and sent the shopper back with ?customer_posted=true.
   * Greet them with the same confirmation the AJAX path shows, and take the
   * parameter off the URL so a refresh does not repeat the greeting.
   */
  if (/[?&]customer_posted=true/.test(location.search)) {
    var pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem('cubedNotifyPending'));
      sessionStorage.removeItem('cubedNotifyPending');
    } catch (err) { /* nothing parked */ }
    if (pending) {
      askEl.hidden = true;
      doneEl.hidden = false;
      if (doneText) {
        doneText.textContent = pending.label
          ? "You'll be the first to know when the " + pending.label + ' combo is back in stock!'
          : "You'll be the first to know when it's back in stock!";
      }
      dialog.showModal();
      closeTimer = setTimeout(close, CLOSE_AFTER + 900);
      var cleaned = location.search.replace(/([?&])customer_posted=true&?/, '$1').replace(/[?&]$/, '');
      history.replaceState(null, '', location.pathname + cleaned + location.hash);
    }
  }
})();
